import { ChevronDown, ChevronUp, Gauge, RotateCcw } from 'lucide-react';
import { useEffect, useRef, useState, type FC } from 'react';

import { useMotion, type MotionStatus, type MotionTelemetry } from '@/hooks/useMotion';
import { useRideRecorder } from '@/hooks/useRideRecorder';
import type { GpsPosition } from '@/types/geo';

const VIEW_KEY = 'motogo.telemetry.view';
type TelemetryView = 'gg' | 'lean';

// Chart scales — road riding peaks around 1g braking / 1.1g lateral on sport
// rubber, so 1.5 leaves headroom without wasting the plot on empty space.
const GG_MAX_G = 1.5;
const BAR_MAX_G = 1.2;
const SWIPE_MIN_PX = 60;

function loadView(): TelemetryView {
  try {
    return localStorage.getItem(VIEW_KEY) === 'lean' ? 'lean' : 'gg';
  } catch {
    return 'gg';
  }
}

function storeView(view: TelemetryView): void {
  try {
    localStorage.setItem(VIEW_KEY, view);
  } catch {
    // non-fatal
  }
}

// ── G-G diagram ─────────────────────────────────────────────────────────────

const GGDiagram: FC<{ telemetry: MotionTelemetry }> = ({ telemetry }) => {
  const canvasRef = useRef<HTMLCanvasElement>(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    const ctx = canvas?.getContext('2d');
    if (!canvas || !ctx) return;

    const dpr = window.devicePixelRatio || 1;
    const w = canvas.clientWidth;
    const h = canvas.clientHeight;
    if (canvas.width !== w * dpr || canvas.height !== h * dpr) {
      canvas.width = w * dpr;
      canvas.height = h * dpr;
    }
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    ctx.clearRect(0, 0, w, h);

    const cx = w / 2;
    const cy = h / 2;
    const radius = Math.min(w, h) / 2 - 16;
    const toX = (latG: number) => cx + (latG / GG_MAX_G) * radius;
    const toY = (longG: number) => cy - (longG / GG_MAX_G) * radius;

    // Grid: crosshair + rings each 0.5g
    ctx.strokeStyle = 'rgba(255, 255, 255, 0.09)';
    ctx.lineWidth = 1;
    ctx.beginPath();
    ctx.moveTo(cx - radius, cy);
    ctx.lineTo(cx + radius, cy);
    ctx.moveTo(cx, cy - radius);
    ctx.lineTo(cx, cy + radius);
    ctx.stroke();
    ctx.fillStyle = '#646b76';
    ctx.font = '10px system-ui, sans-serif';
    ctx.textAlign = 'left';
    ctx.textBaseline = 'top';
    for (const g of [0.5, 1.0, 1.5]) {
      const r = (g / GG_MAX_G) * radius;
      ctx.beginPath();
      ctx.arc(cx, cy, r, 0, Math.PI * 2);
      ctx.stroke();
      ctx.fillText(`${g.toFixed(1)}`, cx + 3, cy - r + 3);
    }

    // Axis labels
    ctx.fillStyle = '#9aa1ab';
    ctx.font = '600 11px system-ui, sans-serif';
    ctx.textAlign = 'center';
    ctx.textBaseline = 'bottom';
    ctx.fillText('ACCEL', cx, cy - radius - 2);
    ctx.textBaseline = 'top';
    ctx.fillText('BRAKE', cx, cy + radius + 2);
    ctx.textBaseline = 'middle';
    ctx.textAlign = 'right';
    ctx.fillText('L', cx - radius - 4, cy);
    ctx.textAlign = 'left';
    ctx.fillText('R', cx + radius + 4, cy);

    // Trail, oldest (faintest) first
    const now = Date.now();
    for (const s of telemetry.trail) {
      const age = now - s.at;
      const alpha = Math.max(0, 1 - age / 4000) * 0.5;
      if (alpha <= 0) continue;
      ctx.fillStyle = `rgba(255, 176, 32, ${alpha.toFixed(3)})`;
      ctx.beginPath();
      ctx.arc(toX(s.latG), toY(s.longG), 2.5, 0, Math.PI * 2);
      ctx.fill();
    }

    // Current point
    ctx.fillStyle = '#ffb020';
    ctx.strokeStyle = '#f2f3f5';
    ctx.lineWidth = 2;
    ctx.beginPath();
    ctx.arc(toX(telemetry.latG), toY(telemetry.longG), 7, 0, Math.PI * 2);
    ctx.fill();
    ctx.stroke();
  }, [telemetry]);

  return (
    <div className="mg-gg-wrap">
      <canvas ref={canvasRef} className="mg-gg-canvas" />
      <div className="mg-gg-peaks">
        <span>
          peak brake <strong>{telemetry.peakBrakeG.toFixed(2)}g</strong>
        </span>
        <span>
          peak accel <strong>{telemetry.peakAccelG.toFixed(2)}g</strong>
        </span>
        <span>
          peak lat <strong>{telemetry.peakLatG.toFixed(2)}g</strong>
        </span>
      </div>
    </div>
  );
};

// ── Lean + acceleration bar ─────────────────────────────────────────────────

const LeanView: FC<{ telemetry: MotionTelemetry }> = ({ telemetry }) => {
  const lean = telemetry.leanImuDeg ?? 0;
  const accelPct = (Math.min(Math.abs(telemetry.longG), BAR_MAX_G) / BAR_MAX_G) * 50;
  const accelerating = telemetry.longG >= 0;

  return (
    <div className="mg-lean-view">
      <div className="mg-lean-readout">
        <span className="mg-lean-number">{Math.abs(Math.round(lean))}°</span>
        <span className="mg-lean-side">{Math.round(lean) === 0 ? ' ' : lean > 0 ? 'RIGHT' : 'LEFT'}</span>
      </div>

      {/* Lean indicator: a bike-up bar over a fixed horizon, ticks every 15° */}
      <svg className="mg-lean-gauge" viewBox="-100 -95 200 105">
        {[-45, -30, -15, 0, 15, 30, 45].map((deg) => {
          const rad = (deg * Math.PI) / 180;
          const x = Math.sin(rad);
          const y = -Math.cos(rad);
          return (
            <line
              key={deg}
              x1={x * 78}
              y1={y * 78}
              x2={x * 86}
              y2={y * 86}
              stroke={deg === 0 ? '#9aa1ab' : 'rgba(255,255,255,0.16)'}
              strokeWidth={2}
            />
          );
        })}
        <line x1={-95} y1={0} x2={95} y2={0} stroke="rgba(255,255,255,0.09)" strokeWidth={2} />
        <g transform={`rotate(${lean})`}>
          <line x1={0} y1={0} x2={0} y2={-66} stroke="#ffb020" strokeWidth={6} strokeLinecap="round" />
          <circle cx={0} cy={-66} r={8} fill="#ffb020" />
        </g>
      </svg>

      {/* Longitudinal g bar: brake grows left (red), accel grows right (green) */}
      <div className="mg-accel-bar">
        <div
          className={accelerating ? 'mg-accel-fill accel' : 'mg-accel-fill brake'}
          style={{ width: `${accelPct}%` }}
        />
        <div className="mg-accel-center" />
      </div>
      <div className="mg-accel-labels">
        <span>BRAKE</span>
        <span className="mg-accel-value">{telemetry.longG.toFixed(2)} g</span>
        <span>ACCEL</span>
      </div>
    </div>
  );
};

// ── Calibration pane ────────────────────────────────────────────────────────

const CalibrationPane: FC<{
  status: MotionStatus;
  hasSaved: boolean;
  onCalibrate: () => void;
  onRestore: () => void;
}> = ({ status, hasSaved, onCalibrate, onRestore }) => {
  if (status === 'unsupported') {
    return (
      <div className="mg-cal-pane">
        <Gauge size={40} />
        <p>This device doesn&apos;t expose motion sensors to the browser.</p>
      </div>
    );
  }
  if (status === 'denied') {
    return (
      <div className="mg-cal-pane">
        <Gauge size={40} />
        <p>
          Motion access was denied. iOS only asks once — enable Motion &amp; Orientation access for
          this site in Settings &gt; Apps &gt; Safari, then reload.
        </p>
        <button className="mg-btn mg-btn-outline" onClick={onCalibrate}>
          Try again
        </button>
      </div>
    );
  }
  if (status === 'calibrating' || status === 'requesting') {
    return (
      <div className="mg-cal-pane">
        <Gauge size={40} className="mg-cal-pulse" />
        <p className="mg-cal-pulse">
          {status === 'requesting' ? 'Waiting for motion sensors…' : 'Hold the bike upright and still…'}
        </p>
      </div>
    );
  }
  return (
    <div className="mg-cal-pane">
      <Gauge size={40} />
      {status === 'calibration-failed' ? (
        <p>Too much movement — hold the bike straight upright and still, then try again.</p>
      ) : (
        <p>
          Telemetry needs a quick calibration. With your phone mounted, hold the bike straight
          upright — off the side or center stand, as if riding — and completely still, then tap
          Calibrate. Your browser may ask for motion access.
        </p>
      )}
      <button className="mg-btn mg-btn-primary" style={{ width: 'auto', padding: '12px 28px' }} onClick={onCalibrate}>
        {status === 'calibration-failed' ? 'Retry calibration' : 'Calibrate'}
      </button>
      {hasSaved && (
        <>
          <button className="mg-btn mg-btn-outline" style={{ width: 'auto', padding: '12px 28px' }} onClick={onRestore}>
            Use previous calibration
          </button>
          <p className="mg-cal-note">
            Assumes the phone sits exactly as it did last time — a shifted mount skews every
            reading. Recalibrate when in doubt.
          </p>
        </>
      )}
    </div>
  );
};

// ── Drawer shell ────────────────────────────────────────────────────────────

const VIEWS: TelemetryView[] = ['gg', 'lean'];

interface Props {
  /**
   * Latest GPS fix — feeds the kinematic lean and the long/lat complementary
   * blends. The whole object (not just speed): a fresh object per fix is what
   * advances the speed derivative through identical readings.
   */
  position: GpsPosition | null;
}

const TelemetryDrawer: FC<Props> = ({ position }) => {
  const [open, setOpen] = useState(false);
  const [view, setView] = useState<TelemetryView>(loadView);
  const { status, telemetry, calibrate, hasSavedCalibration, restoreCalibration } = useMotion(position);
  const speedMps = position?.speed ?? null;
  const { sampleTelemetry } = useRideRecorder();
  const touchStartX = useRef<number | null>(null);

  // While recording, feed this (single, already-calibrated) motion stream to the recorder,
  // which accumulates the per-interval peaks + the G-G pair per stored point. No-op when
  // not recording / not calibrated. Reusing the drawer's instance means iOS calibration
  // (its requestPermission must come from a tap) carries over to capture.
  useEffect(() => {
    if (status !== 'active') return;
    sampleTelemetry({
      longG: telemetry.longG,
      latG: telemetry.latG,
      leanDeg: telemetry.leanImuDeg ?? telemetry.leanKinDeg ?? null,
    });
  }, [telemetry, status, sampleTelemetry]);

  function selectView(next: TelemetryView) {
    setView(next);
    storeView(next);
  }

  function handleTouchStart(e: React.TouchEvent) {
    touchStartX.current = e.touches[0].clientX;
  }

  function handleTouchEnd(e: React.TouchEvent) {
    const start = touchStartX.current;
    touchStartX.current = null;
    if (start == null) return;
    const delta = e.changedTouches[0].clientX - start;
    if (Math.abs(delta) < SWIPE_MIN_PX) return;
    const next = VIEWS[(VIEWS.indexOf(view) + (delta < 0 ? 1 : VIEWS.length - 1)) % VIEWS.length];
    selectView(next);
  }

  const kph = speedMps != null ? Math.round(speedMps * 3.6) : null;

  return (
    <div className={open ? 'mg-drawer open' : 'mg-drawer'}>
      <div className="mg-drawer-panel" onTouchStart={handleTouchStart} onTouchEnd={handleTouchEnd}>
        {/* All flow content lives in this clipping wrapper: on short (landscape) viewports
            the calibration pane is taller than the panel, and unclipped overflow would
            spill out below the closed (translated-up) panel — ghost text over the map
            that also swallows every tap. The tab and scrim stay outside the clip. */}
        <div className="mg-drawer-body">
        {status === 'active' ? (
          <>
            <div className="mg-drawer-header">
              <div className="mg-drawer-dots">
                {VIEWS.map((v) => (
                  <button
                    key={v}
                    className={v === view ? 'mg-drawer-dot active' : 'mg-drawer-dot'}
                    onClick={() => selectView(v)}
                    aria-label={v === 'gg' ? 'G-G diagram' : 'Lean angle'}
                  />
                ))}
              </div>
              <button
                className="mg-icon-btn"
                onClick={() => void calibrate()}
                title="Recalibrate"
                aria-label="Recalibrate"
              >
                <RotateCcw size={22} />
              </button>
            </div>
            {view === 'gg' ? <GGDiagram telemetry={telemetry} /> : <LeanView telemetry={telemetry} />}
            {/* Debug strip — both lean methods side by side, to reference against
                the bike's own IMU. KIN needs GPS speed; IMU drifts in long corners. */}
            <div className="mg-drawer-debug">
              <span>
                IMU {telemetry.leanImuDeg != null ? `${telemetry.leanImuDeg.toFixed(1)}°` : '—'}
              </span>
              <span>
                KIN {telemetry.leanKinDeg != null ? `${telemetry.leanKinDeg.toFixed(1)}°` : '—'}
              </span>
              <span>lat {telemetry.latG.toFixed(2)}g</span>
              <span>long {telemetry.longG.toFixed(2)}g</span>
              <span>{kph != null ? `${kph} km/h` : '— km/h'}</span>
            </div>
          </>
        ) : (
          <CalibrationPane
            status={status}
            hasSaved={hasSavedCalibration}
            onCalibrate={() => void calibrate()}
            onRestore={() => void restoreCalibration()}
          />
        )}
        </div>
        {/* Tap-catcher over the map sliver below the open drawer — tapping the
            visible bit of map closes the drawer (it sits above the map but below
            the bottom bar's area, and the tab renders after it, so both stay tappable). */}
        {open && <div className="mg-drawer-scrim" onClick={() => setOpen(false)} aria-hidden="true" />}
        <button
          className="mg-drawer-tab"
          onClick={() => setOpen(!open)}
          aria-label={open ? 'Close telemetry' : 'Open telemetry'}
        >
          {open ? <ChevronUp size={20} /> : (
            <>
              <Gauge size={18} />
              <ChevronDown size={20} />
            </>
          )}
        </button>
      </div>
    </div>
  );
};

export default TelemetryDrawer;
